import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import AccordionScrapRulesOverview from "../../components/AccordionOverview";
import AccordionScrapRulesEquipments from "../../components/AccordionEquipments";
import { CreateScrapRuleSchema, createScrapRuleSchema } from "../../schemas/createScrapRule.schema";
import AccordionScrapRulesExecutionRules from "../../components/AccordionExecutionRules";
import useScrapRule from "../../hooks/useScrapRule";
import { ModalDeleteScrapRule } from "../../components/ModalDelete";
import { ScrapRulesTransformer } from "../../services/scrapRulesTransformer";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const Show = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: scrapRule,
    isLoading: scrapRuleIsLoading,
    error: scrapRuleError = {} as any,
    refetch: refetchScrapRule
   } = useScrapRule(String(id))

  const {
    register,
    control,
    reset,
    formState: { errors }
  } = useForm<CreateScrapRuleSchema>({
    resolver: zodResolver(createScrapRuleSchema)
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToSeeMaintenanceScrapRules =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::ScrapRules::Show')

  useEffect(() => {
    if (!scrapRuleIsLoading && scrapRule) {
      const formData = ScrapRulesTransformer.formDataTransformer(scrapRule)
      reset(formData)
    }
  }, [scrapRuleIsLoading])

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/maintenance-scrap-rules`,
          searchLastRouteFromFallback: true
        }}
        title="Regras para desfazimento"
        subtitle={scrapRule?.display_name}
        buttons={
          userHasPermissionToSeeMaintenanceScrapRules ? (
            <div className="flex items-center gap-4">
              <ModalDeleteScrapRule scrapRuleId={id} />
              <Button
                operationKlassToCheckPermission="Client::Maintenance::ScrapRules::Update"
                onClick={() => navigate('edit')}
              >
                Editar
              </Button>
            </div>
          ) : undefined
        }
      />

      <ErrorMessageWrapper
        statusCode={scrapRuleError?.status}
        refetchFunction={refetchScrapRule}
      >
        <Box className="flex flex-col gap-4 flex-1 overflow-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionScrapRulesOverview />

          <div className="flex gap-2">
            <TextField
              label="Nome da regra de desfazimento"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              {...register('display_name')}
              className="w-full"
              disabled
            />
          </div>

          <AccordionScrapRulesEquipments
            control={control}
            register={register}
            errors={errors}
            disabled
          />

          <AccordionScrapRulesExecutionRules
            control={control}
            register={register}
            errors={errors}
            scrapRule={scrapRule}
            disabled
          />
        </Box>
        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </div>
  );
};

export default Show;
